import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Modal,
  message,
} from "antd";
import { DeleteOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { createFinancialRecord } from "../../api/host";
import { toBjNoon, fmtBj, today, r2 } from "../../utils/bjDate";
import {
  BILL_TYPE_MAP,
  BILL_TYPE_COLOR,
  PAYMENT_METHOD_MAP,
} from "./rentBillConstants";

const FMT = "YYYY/MM/DD";
const METHOD_OPTIONS = ["wechat", "alipay", "cash", "transfer"].map((v) => ({
  value: v,
  label: PAYMENT_METHOD_MAP[v],
}));
const n2 = (v) =>
  v == null
    ? "-"
    : Number(v).toLocaleString("zh-CN", { maximumFractionDigits: 2 });

const css = `
.rsd{max-width:560px;margin:0 auto;}
.rsd-row{display:flex;align-items:flex-start;gap:8px;margin-bottom:10px;}
.rsd-label{flex:0 0 4.4em;width:4.4em;line-height:32px;font-size:13px;color:#64748b;white-space:nowrap;overflow:hidden;transition:flex-basis .2s,width .2s,opacity .2s;}
.rsd-row:focus-within>.rsd-label{flex-basis:0;width:0;opacity:0;}
.rsd-cell{min-width:0;}
.rsd-sec{display:flex;align-items:center;gap:8px;margin:4px 0 8px;font-size:13px;}
.rsd-sec b{font-size:14px;color:#0f172a;}
.rsd-sum{margin-left:auto;font-variant-numeric:tabular-nums;font-weight:600;}
.rsd-card{--c:#94a3b8;margin-bottom:8px;padding:8px 10px 8px 12px;border-radius:10px;border:1px solid rgba(148,163,184,.3);
  border-left:4px solid var(--c);background:#fff;}
.rsd-card-top{display:flex;align-items:center;gap:8px;}
.rsd-type{display:inline-block;padding:0 8px;border-radius:7px;background:var(--tc);color:#fff;font-size:12px;font-weight:600;line-height:22px;white-space:nowrap;}
.rsd-mute{color:#94a3b8;font-size:12px;}
.rsd-meta{margin-top:4px;font-size:12px;color:#94a3b8;}
.rsd-v{display:flex;gap:8px;padding:6px 0;font-size:14px;line-height:1.6;color:#0f172a;border-bottom:1px solid rgba(148,163,184,.18);}
.rsd-vl{flex:0 0 4em;color:#94a3b8;font-size:13px;}
.rsd-vv{flex:1;min-width:0;word-break:break-word;white-space:pre-wrap;}
.rsd-head{display:flex;align-items:baseline;justify-content:space-between;padding:2px 0 8px;border-bottom:1px solid rgba(148,163,184,.18);}
.rsd-amt{font-size:22px;font-weight:700;color:#16a34a;font-variant-numeric:tabular-nums;}
.rsd-foot{display:flex;align-items:center;justify-content:flex-end;gap:8px;max-width:560px;margin:0 auto;}
.rsd-hint{margin-right:auto;font-size:12px;}
`;

const Row = ({ label, children }) => (
  <div className="rsd-row">
    {label && <label className="rsd-label">{label}</label>}
    {children}
  </div>
);
const Cell = ({ flex = 1, children }) => (
  <div className="rsd-cell" style={{ flex }}>
    {children}
  </div>
);
const Item = (props) => <Form.Item style={{ marginBottom: 0 }} {...props} />;
const TypeChip = ({ type }) => (
  <span
    className="rsd-type"
    style={{ "--tc": BILL_TYPE_COLOR[type] || "#64748b" }}
  >
    {BILL_TYPE_MAP[type] || type}
  </span>
);
const View = ({ label, children }) => (
  <div className="rsd-v">
    <span className="rsd-vl">{label}</span>
    <span className="rsd-vv">{children}</span>
  </div>
);

// 按顺序冲抵：把 available 依次分配给每个账单
const allocate = (list, available) => {
  let remaining = r2(available);
  return list.map((item) => {
    const should = r2(item.billShouldPayAmount);
    if (remaining <= 0) return { ...item, allocatedAmount: 0 };
    const a = r2(Math.min(remaining, should));
    remaining = r2(remaining - a);
    return { ...item, allocatedAmount: a };
  });
};

/**
 * 结算抽屉：创建(create) / 只读(view)
 * - record 为空：创建，对 bills(待付款账单) 分配本次付款
 * - record 有值：只读，展示一次结算（付款记录）的详情
 */
export default function SettlementDrawer({
  open,
  onCancel,
  contract,
  bills = [],
  record = null,
  billChange,
}) {
  const [form] = Form.useForm();
  const inputRef = useRef(null);
  const methodRef = useRef(null);
  const [dataSource, setDataSource] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [allocTick, setAllocTick] = useState(0);
  const [dateOpen, setDateOpen] = useState(false);
  const [methodOpen, setMethodOpen] = useState(false);
  const dateRef = useRef(null);
  const depositRef = useRef(null);
  const remarkRef = useRef(null);
  const viewMode = !!record;
  const paymentAmount = Form.useWatch("paymentAmount", form) || 0;
  const depositDeduction = Form.useWatch("depositDeduction", form) || 0;
  const paymentMethod = Form.useWatch("paymentMethod", form);
  const paymentDate = Form.useWatch("paymentDate", form);

  const billMeta = useMemo(
    () => Object.fromEntries(bills.map((b) => [b._id, b])),
    [bills],
  );
  const totalAllocated = useMemo(
    () => r2(dataSource.reduce((s, i) => s + (i.allocatedAmount || 0), 0)),
    [dataSource],
  );
  const available = r2(paymentAmount + depositDeduction);
  const diff = r2(available - totalAllocated);
  const canSubmit =
    totalAllocated > 0 && diff === 0 && !!paymentMethod && !!paymentDate;

  // 初始化（创建模式）
  useEffect(() => {
    if (!open || viewMode) return;
    const pending = contract?.status === "pendingSettlement";
    const list = bills.map((b) => ({
      billId: b._id,
      roomNumber: `${b?.host?.roomNumber}`,
      billType: { val: b.billType, text: BILL_TYPE_MAP[b.billType] },
      billAmount: b.amount,
      billPaidAmount: b?.paidAmount || 0,
      billShouldPayAmount: r2(b.amount - (b?.paidAmount || 0)),
      allocatedAmount: pending ? r2(b.amount - (b?.paidAmount || 0)) : 0,
    }));
    setDataSource(list);
    form.setFieldsValue({
      paymentDate: today(),
      paymentAmount: pending
        ? r2(
            list.reduce((s, i) => s + i.billShouldPayAmount, 0) -
              (contract.securityDepositPayed || 0),
          )
        : null,
      depositDeduction: pending ? contract.securityDepositPayed || null : null,
      remark: pending ? "结算自动分配" : "",
    });
    setAllocTick((t) => t + 1);
    // 待结算（退房结算）：金额、押金已自动带出，直接聚焦付款方式；其他情况聚焦实际付款金额
    const t = setTimeout(
      () => (pending ? methodRef.current : inputRef.current)?.focus?.(),
      300,
    );
    return () => clearTimeout(t);
  }, [open, record, bills]);

  // 关闭后清空
  useEffect(() => {
    if (!open) {
      form.resetFields();
      setDataSource([]);
    }
  }, [open]);

  // 自动分配：只在 付款金额 / 押金抵扣 / 手动触发 变化时执行（不依赖 dataSource，避免死循环和覆盖手动分配）
  useEffect(() => {
    if (!open || viewMode) return;
    const timer = setTimeout(
      () =>
        setDataSource((prev) =>
          allocate(prev, r2(paymentAmount + depositDeduction)),
        ),
      450,
    );
    return () => clearTimeout(timer);
  }, [open, viewMode, paymentAmount, depositDeduction, allocTick]);

  const handleAmountChange = (value, item) => {
    const v = r2(value || 0);
    if (v > item.billShouldPayAmount)
      return message.warning("分配金额不能超过待结金额");
    if (r2(totalAllocated - item.allocatedAmount + v) > available)
      return message.warning("累计分配不能超过实际付款金额");
    setDataSource((prev) =>
      prev.map((x) =>
        x.billId === item.billId ? { ...x, allocatedAmount: v } : x,
      ),
    );
  };
  const handleDelete = (id) => {
    setDataSource((prev) => prev.filter((x) => x.billId !== id));
    setAllocTick((t) => t + 1); // 删除后重新冲抵
  };

  const close = () => {
    form.resetFields();
    onCancel();
  };
  const requestClose = () => {
    if (!viewMode && form.isFieldsTouched()) {
      Modal.confirm({
        title: "放弃本次结算？",
        okText: "放弃",
        cancelText: "继续填写",
        onOk: close,
      });
    } else close();
  };

  const handleSubmit = async () => {
    const paidRecords = dataSource
      .filter((x) => x.allocatedAmount > 0)
      .map(({ roomNumber, ...x }) => ({ ...x, billType: x.billType.val })); // 后端只要类型值
    if (!paidRecords.length) return message.error("请至少为一个账单分配金额");
    let info;
    try {
      info = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      await createFinancialRecord({
        ...info,
        depositDeduction: info.depositDeduction ?? 0, // 不填按 0
        paymentDate: toBjNoon(info.paymentDate),
        hostRentBillPaidRecords: paidRecords,
      });
      message.success("结算成功");
      close();
      billChange?.();
    } catch (e) {
      message.error(e?.response?.data?.message || "结算失败");
    } finally {
      setSubmitting(false);
    }
  };

  const details = record?.billPaidDetails || [];
  const detailTotal = r2(
    details.reduce((s, d) => s + (d.allocatedAmount || 0), 0),
  );
  const deposit = Number(contract?.securityDepositPayed) || 0;

  return (
    <Drawer
      placement="top"
      open={open}
      onClose={requestClose}
      closable={false}
      height="auto"
      styles={{
        wrapper: { maxHeight: "92vh" },
        body: {
          padding: "calc(12px + env(safe-area-inset-top, 0px)) 12px 4px",
        },
        footer: {
          padding: "8px 12px calc(8px + env(safe-area-inset-bottom, 0px))",
        },
      }}
      footer={
        <div className="rsd-foot">
          {viewMode ? (
            <Button onClick={close}>关闭</Button>
          ) : (
            <>
              {diff !== 0 && totalAllocated > 0 && (
                <span
                  className="rsd-hint"
                  style={{ color: diff > 0 ? "#d97706" : "#dc2626" }}
                >
                  {diff > 0 ? `还有 ¥${n2(diff)} 未分配` : `超出 ¥${n2(-diff)}`}
                </span>
              )}
              <Button onClick={requestClose} disabled={submitting}>
                取消
              </Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={submitting}
                disabled={!canSubmit}
              >
                提交结算
              </Button>
            </>
          )}
        </div>
      }
    >
      <style>{css}</style>
      <div className="rsd">
        <Form form={form} component="div">
          {viewMode ? (
            <>
              <div className="rsd-head">
                <span className="rsd-amt">¥{n2(record.paymentAmount)}</span>
                <span style={{ color: "#475569" }}>
                  {fmtBj(record.paymentDate, FMT)}
                </span>
              </div>
              <View label="付款方式">
                {PAYMENT_METHOD_MAP[record.paymentMethod] ||
                  record.paymentMethod ||
                  "-"}
              </View>
              {Number(record.depositDeduction) > 0 && (
                <View label="押金抵扣">¥{n2(record.depositDeduction)}</View>
              )}
              {record.recordNo && <View label="流水号">{record.recordNo}</View>}
              {record.remark ? <View label="备注">{record.remark}</View> : null}
              <div className="rsd-sec" style={{ marginTop: 12 }}>
                <b>分配明细</b>
                <span className="rsd-sum" style={{ color: "#16a34a" }}>
                  合计 ¥{n2(detailTotal)}
                </span>
              </div>
              {details.length === 0 && (
                <div className="rsd-mute">无分配明细</div>
              )}
              {details.map((d, i) => {
                const settled = d.allocatedAmount >= d.billShouldPayAmount;
                return (
                  <div
                    key={d.billId || i}
                    className="rsd-card"
                    style={{ "--c": settled ? "#16a34a" : "#d97706" }}
                  >
                    <div className="rsd-card-top">
                      <TypeChip type={d.billType} />
                      <span className="rsd-mute">
                        待结 ¥{n2(d.billShouldPayAmount)}
                      </span>
                      <b style={{ marginLeft: "auto", color: "#16a34a" }}>
                        ¥{n2(d.allocatedAmount)}
                      </b>
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <>
              <Row>
                <Cell flex={1.2}>
                  <Item
                    name="paymentAmount"
                    rules={[{ required: true, message: "请输入付款金额" }]}
                  >
                    <InputNumber
                      ref={inputRef}
                      controls={false}
                      prefix="¥"
                      precision={2}
                      placeholder="实际付款金额"
                      inputMode="decimal"
                      style={{ width: "100%" }}
                      onPressEnter={() => setMethodOpen(true)}
                      enterKeyHint="next"
                    />
                  </Item>
                </Cell>
                <Cell>
                  <Item
                    name="paymentMethod"
                    rules={[{ required: true, message: "请选择付款方式" }]}
                  >
                    <Select
                      ref={methodRef}
                      options={METHOD_OPTIONS}
                      placeholder="付款方式"
                      open={methodOpen}
                      onOpenChange={setMethodOpen}
                      onChange={() => {
                        setMethodOpen(false);
                        dateRef.current?.focus(); // 选完付款方式 -> 付款日期（只聚焦，不弹面板）
                      }}
                    />
                  </Item>
                </Cell>
              </Row>
              <Row>
                {/* 日期面板只在点击时打开，用输入法"下一项"聚焦到这里不弹出 */}
                <Cell>
                  <div
                    onClick={(e) => {
                      if (e.currentTarget.contains(e.target)) setDateOpen(true); // 面板(portal)里冒泡上来的不算
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        e.currentTarget.contains(e.target)
                      ) {
                        e.preventDefault();
                        setDateOpen(false);
                        depositRef.current?.focus();
                      }
                    }}
                  >
                    <Item
                      name="paymentDate"
                      rules={[{ required: true, message: "请选择付款日期" }]}
                    >
                      <DatePicker
                        ref={dateRef}
                        open={dateOpen}
                        onOpenChange={(o) => !o && setDateOpen(false)}
                        placeholder="付款日期"
                        format={FMT}
                        inputReadOnly
                        allowClear={false}
                        suffixIcon={null}
                        style={{ width: "100%" }}
                      />
                    </Item>
                  </div>
                </Cell>
                <Cell>
                  <Item name="depositDeduction">
                    <InputNumber
                      ref={depositRef}
                      onPressEnter={() => remarkRef.current?.focus()}
                      enterKeyHint="next"
                      controls={false}
                      min={0}
                      max={deposit}
                      precision={2}
                      placeholder="押金抵扣"
                      inputMode="decimal"
                      addonAfter={`/${deposit}`}
                      style={{ width: "100%" }}
                    />
                  </Item>
                </Cell>
              </Row>
              <Row label="备注">
                <Cell>
                  <Item name="remark">
                    <Input.TextArea
                      ref={remarkRef}
                      autoSize={{ minRows: 1, maxRows: 4 }}
                      maxLength={200}
                    />
                  </Item>
                </Cell>
              </Row>

              <div className="rsd-sec">
                <b>分配明细</b>
                <Button
                  size="small"
                  icon={<ThunderboltOutlined />}
                  onClick={() => setDataSource((p) => allocate(p, available))}
                >
                  自动分配
                </Button>
                <span
                  className="rsd-sum"
                  style={{
                    color:
                      diff === 0 && totalAllocated > 0 ? "#16a34a" : "#d97706",
                  }}
                >
                  ¥{n2(totalAllocated)} / ¥{n2(available)}
                </span>
              </div>
              {dataSource.map((item) => {
                const meta = billMeta[item.billId];
                const settled =
                  item.allocatedAmount >= item.billShouldPayAmount;
                const c =
                  item.allocatedAmount <= 0
                    ? "#94a3b8"
                    : settled
                      ? "#16a34a"
                      : "#d97706";
                return (
                  <div
                    key={item.billId}
                    className="rsd-card"
                    style={{ "--c": c }}
                  >
                    <div className="rsd-card-top">
                      <TypeChip type={item.billType.val} />
                      <span className="rsd-mute">
                        待结 ¥{n2(item.billShouldPayAmount)}
                      </span>
                      <span
                        style={{
                          marginLeft: "auto",
                          display: "inline-flex",
                          gap: 4,
                        }}
                      >
                        <InputNumber
                          controls={false}
                          size="small"
                          prefix="¥"
                          min={0}
                          precision={2}
                          inputMode="decimal"
                          value={item.allocatedAmount}
                          onChange={(v) => handleAmountChange(v, item)}
                          style={{ width: 118 }}
                        />
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          aria-label="不结算这条"
                          onClick={() => handleDelete(item.billId)}
                        />
                      </span>
                    </div>
                    {meta && (
                      <div className="rsd-meta">
                        账单日 {fmtBj(meta.dueDate, FMT)}
                        {meta.startDate
                          ? `　${fmtBj(meta.startDate, FMT)} - ${fmtBj(meta.endDate, FMT)}`
                          : ""}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </Form>
      </div>
    </Drawer>
  );
}
